import { useState } from 'react'
import { motion } from 'framer-motion'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#C8923A'
const ACCENT_SOFT = '#E0B870'

// Placeholder — substituir quando o link do Google Drive for fornecido
const CRIATIVOS_LINK = 'https://drive.google.com/drive/folders/1cgl9saf_WXRoRyjDjqO8h-QedlagNfoY?usp=sharing'
const HOTMART_PAINEL = 'https://app.hotmart.com/market/affiliate'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: ACCENT_SOFT }}>
      {children}
    </p>
  )
}

interface ScriptBlockProps {
  id: string
  label: string
  title: string
  text: string
}

function ScriptBlock({ id, label, title, text }: ScriptBlockProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2200)
      }
    }
    catch {
      // ignore
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.7 }}
      className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
      style={{
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <h3 className="font-display type-section mb-5" style={{ color: 'var(--ink, #e8e0d5)' }}>
        {title}
      </h3>

      <div
        className="rounded-sm p-5 sm:p-6 mb-4 whitespace-pre-line font-body type-body leading-relaxed"
        style={{
          background: 'rgba(0,0,0,0.25)',
          border: `1px solid ${ACCENT}1a`,
          color: 'rgba(232,224,213,0.85)',
        }}
      >
        {text}
      </div>

      <button
        type="button"
        onClick={handleCopy}
        aria-label={`Copiar ${title}`}
        className="text-[13px] font-body tracking-[0.04em] px-4 py-2 rounded-sm transition-all duration-300 active:scale-[0.98]"
        style={{
          background: copied ? `${ACCENT}22` : 'transparent',
          color: copied ? ACCENT_SOFT : ACCENT,
          border: `1px solid ${ACCENT}55`,
        }}
        data-script-id={id}
      >
        {copied ? '✓ Copiado' : 'Copiar texto'}
      </button>
    </motion.div>
  )
}

const SCRIPT_GRUPOS = `Quero compartilhar algo que mudou algo dentro de mim.

Fiz o Ciclo 1 do Protocolo de Liberação e Cocriação da Mônica Maria e recebi uma meditação gravada especificamente para o meu campo e um mapa que revelou padrões que eu carregava sem perceber.

[Conte aqui brevemente o que você sentiu — o que mudou, o que se moveu, o que você percebeu.]

Se você sente que algo está travado na sua vida e quer entender o que é, esse é o caminho.

Acessa aqui: [seu link de afiliada]`

const SCRIPT_INDIVIDUAL = `Oi [Nome], tudo bem?

Precisei te contar uma coisa. Fiz um processo que me tocou de verdade — recebi uma meditação personalizada com o meu nome e um mapa que revelou exatamente o que estava operando no meu campo.

[Conte brevemente o que sentiu.]

Pensei em você porque [motivo específico]. Se quiser saber mais me fala que te mando o link.

Passo 1 — Manda o link da jornada gratuita para ela conhecer:
jornada.reacendaluz.com.br

Passo 2 — Quando ela terminar a jornada e quiser continuar, manda o link direto do produto:
[seu link de afiliada]`

const SCRIPT_STORIES = `Frame 1: Você já sentiu que os mesmos padrões se repetem na sua vida sem conseguir mudar?

Frame 2: Eu senti isso. Até conhecer o Protocolo de Liberação e Cocriação da Mônica Maria.

Frame 3: [Descreva em uma frase o que mudou para você.]

Frame 4: Se isso ressoou com você, o link está aqui. [seu link de afiliada]`

const SCRIPT_AUDIO = `Oi, vim compartilhar algo que transformou algo dentro de mim.

Fiz o Ciclo 1 do Protocolo de Liberação e Cocriação e recebi uma meditação gravada especificamente para o meu campo vibracional e um mapa que revelou padrões que eu carregava há anos sem perceber.

[Fale em suas próprias palavras o que sentiu — seja breve e genuína.]

Se você sente que algo está travado e quer entender o que é, clica no link que está na minha bio ou me chama aqui.`

const DICAS = [
  'Fale sempre da sua experiência real.',
  'Nunca prometa resultados específicos — cada campo é único.',
  'Responda as dúvidas de quem se interessar com acolhimento e sem pressão.',
  'Se não souber responder algo, direcione para a Mônica.',
  'Sempre use o link direto do produto como segundo passo após a jornada gratuita para garantir o rastreamento da sua comissão.',
]

export default function PageEstrelasDivulgar() {
  usePageMeta({
    title: 'Como divulgar como Estrela de Luz — Reative os 4 Elementos',
    description: 'Tudo que você precisa para propagar a luz com autenticidade e resultado: scripts prontos, criativos e dicas para Estrelas de Luz.',
    ogUrl: 'https://jornada.reacendaluz.com.br/estrelas/divulgar/',
  })

  const criativosDisponivel = CRIATIVOS_LINK !== '#'

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Estrelas de Luz
        </p>
      </header>

      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >

          {/* HERO */}
          <div className="text-center mb-14">
            <motion.div
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.1 }}
              className="flex justify-center mb-7"
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
                <circle cx="28" cy="28" r="24" stroke={ACCENT} strokeWidth="1" opacity="0.4" />
                {/* Triângulo apontando para cima */}
                <polygon
                  points="28,10 43.5,37 12.5,37"
                  fill={ACCENT}
                  opacity="0.85"
                />
                {/* Triângulo apontando para baixo */}
                <polygon
                  points="28,46 12.5,19 43.5,19"
                  fill={ACCENT}
                  opacity="0.85"
                />
              </svg>
            </motion.div>

            <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: ACCENT_SOFT }}>
              Guia de Divulgação
            </p>

            <h1 className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-5">
              Como divulgar como{' '}
              <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                Estrela de Luz
              </em>
            </h1>

            <p
              className="font-display italic text-[18px] md:text-[20px] mb-8 max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'rgba(232,224,213,0.7)' }}
            >
              Tudo que você precisa para propagar a luz com autenticidade e resultado.
            </p>

            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 80, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              className="h-px mx-auto"
              style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}88, transparent)` }}
            />
          </div>

          {/* BLOCO 1 — Antes de começar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}10 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}28`,
            }}
          >
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}14, transparent)` }}
            />
            <SectionLabel>Antes de começar</SectionLabel>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
              A regra mais importante: <span className="italic" style={{ color: ACCENT_SOFT }}>divulgue só o que você viveu.</span> Fale da sua experiência. Mostre o que mudou no seu campo. Isso converte muito mais do que qualquer técnica de venda.
            </p>
          </motion.div>

          {/* BLOCO 2 — O primeiro passo mais poderoso */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <SectionLabel>O primeiro passo mais poderoso</SectionLabel>
            <p className="font-body type-body leading-relaxed mb-4" style={{ color: 'rgba(232,224,213,0.8)' }}>
              Um jeito natural e genuíno de começar é convidando alguém próximo para fazer a Jornada <em className="italic" style={{ color: ACCENT_SOFT }}>Reative os 4 Elementos</em> gratuitamente. Manda o link e diz:
            </p>
            <blockquote
              className="rounded-sm p-5 mb-5 italic font-display text-[17px] sm:text-[18px] leading-relaxed"
              style={{
                background: 'rgba(0,0,0,0.22)',
                borderLeft: `2px solid ${ACCENT}`,
                color: 'rgba(232,224,213,0.85)',
              }}
            >
              "Se inscreve aqui e quando sair o seu resultado me conta qual foi o seu elemento mais bloqueado."
            </blockquote>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
              Quando ela compartilhar o resultado, acolhe com presença e conta brevemente o que o Protocolo fez pelo seu campo. Esse é o momento certo para enviar o seu link direto do produto — assim o rastreamento da sua comissão fica garantido.
            </p>
          </motion.div>

          {/* BLOCO 3 — Criativos */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}10 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            <div
              className="absolute -bottom-16 -left-16 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}12, transparent)` }}
            />
            <SectionLabel>Criativos prontos para divulgar</SectionLabel>
            <p className="font-body type-body leading-relaxed mb-6" style={{ color: 'rgba(232,224,213,0.8)' }}>
              Baixe aqui as imagens e materiais visuais prontos para usar no WhatsApp, Instagram e stories. Salve na sua galeria e use com o seu link de afiliada.
            </p>

            {criativosDisponivel ? (
              <a
                href={CRIATIVOS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full sm:w-auto sm:inline-block text-center px-7 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] transition-all duration-300 active:scale-[0.98] hover:brightness-110"
                style={{ background: ACCENT, color: '#ffffff' }}
              >
                Baixar criativos
              </a>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="block w-full sm:w-auto sm:inline-block text-center px-7 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] cursor-not-allowed"
                  style={{ background: `${ACCENT}66`, color: '#ffffff' }}
                >
                  Baixar criativos
                </button>
                <p className="text-[13px] italic font-body" style={{ color: `${ACCENT_SOFT}99` }}>
                  Em breve — link do Drive será disponibilizado em instantes.
                </p>
              </div>
            )}
          </motion.div>

          {/* BLOCO 4 — Script grupos WhatsApp */}
          <ScriptBlock
            id="script-grupos"
            label="Bloco 4"
            title="Script para grupos de WhatsApp"
            text={SCRIPT_GRUPOS}
          />

          {/* BLOCO 5 — Script individual */}
          <ScriptBlock
            id="script-individual"
            label="Bloco 5"
            title="Script para mensagem individual no WhatsApp"
            text={SCRIPT_INDIVIDUAL}
          />

          {/* BLOCO 6 — Stories Instagram */}
          <ScriptBlock
            id="script-stories"
            label="Bloco 6"
            title="Script para stories do Instagram"
            text={SCRIPT_STORIES}
          />

          {/* BLOCO 7 — Roteiro de áudio */}
          <ScriptBlock
            id="script-audio"
            label="Bloco 7"
            title="Roteiro de áudio de 1 minuto"
            text={SCRIPT_AUDIO}
          />

          {/* BLOCO 8 — Mapa de Cura */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8"
            style={{
              background: 'rgba(139,94,156,0.08)',
              border: '1px solid rgba(139,94,156,0.28)',
            }}
          >
            <SectionLabel>Sobre o Mapa de Cura dos 4 Elementos</SectionLabel>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
              Você também pode indicar o <span className="font-medium" style={{ color: 'var(--ink, #e8e0d5)' }}>Mapa de Cura dos 4 Elementos</span> por <span className="numerals">R$147</span> para quem ainda não está pronta para o Protocolo completo. É uma porta de entrada poderosa e você recebe aproximadamente <span className="numerals">R$65,73</span> por cada venda. Use o link de afiliada do Mapa gerado na Hotmart.
            </p>
          </motion.div>

          {/* BLOCO 9 — Dicas + CTA painel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.7 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${ACCENT}0a 0%, rgba(255,255,255,0.02) 100%)`,
              border: `1px solid ${ACCENT}33`,
            }}
          >
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${ACCENT}14, transparent)` }}
            />
            <SectionLabel>Dicas importantes</SectionLabel>
            <ul className="space-y-4 mb-8">
              {DICAS.map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5" style={{ color: ACCENT, fontSize: '14px', lineHeight: 1.7 }}>
                    ✦
                  </span>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {text}
                  </p>
                </li>
              ))}
            </ul>

            {/* Bloco — Como encontrar seu link de afiliada */}
            <div
              className="rounded-sm p-5 sm:p-6 mb-8 mt-2"
              style={{
                background: 'rgba(0,0,0,0.22)',
                border: `1px solid ${ACCENT}28`,
              }}
            >
              <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-4" style={{ color: ACCENT_SOFT }}>
                Passo a passo
              </p>
              <h4 className="font-display type-section mb-5" style={{ color: 'var(--ink, #e8e0d5)' }}>
                Como encontrar seu link de afiliada na Hotmart
              </h4>
              <ol className="space-y-3 mb-6">
                {[
                  'Acessa sua conta na Hotmart e clica no ícone de menu no canto superior esquerdo.',
                  'Vai em Produtos e em seguida em Meus Produtos.',
                  'Clica na aba Sou Afiliado.',
                  'Localiza o produto desejado e clica nele.',
                  'No menu lateral interno clica em Links de Divulgação.',
                  'Escolhe o link da página de pagamento — Checkout direto — e copia.',
                  'Se for divulgar o link da Jornada Reative os 4 Elementos na mesma página Links de Divulgação clica na aba Links adicionais e copia o link que vai divulgar.',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 numerals font-medium text-[13px] mt-0.5 w-5 text-right"
                      style={{ color: ACCENT }}
                    >
                      {i + 1}.
                    </span>
                    <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.82)' }}>
                      {step}
                    </p>
                  </li>
                ))}
              </ol>

              {/* Dica */}
              <div
                className="rounded-sm p-4 sm:p-5 mb-6"
                style={{
                  background: `${ACCENT}0d`,
                  border: `1px solid ${ACCENT}30`,
                }}
              >
                <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.85)' }}>
                  <span className="font-medium" style={{ color: ACCENT_SOFT }}>Dica:</span>{' '}
                  É esse endereço que contém o seu código de afiliada único — por exemplo{' '}
                  <span className="numerals font-medium" style={{ color: ACCENT_SOFT }}>go.hotmart.com/XYZ</span>{' '}
                  — garantindo que a comissão da venda seja creditada a você.
                </p>
              </div>

              {/* Link YouTube */}
              <p className="font-body type-body leading-relaxed mb-7" style={{ color: 'rgba(232,224,213,0.75)' }}>
                Se tiver dúvidas assiste esse passo a passo no YouTube.{' '}
                <a
                  href="https://www.youtube.com/watch?v=law5kx2OI44"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 transition-opacity duration-200 hover:opacity-80"
                  style={{ color: ACCENT_SOFT }}
                >
                  Assistir o passo a passo
                </a>
              </p>

              <div className="text-center">
                <a
                  href="https://app.hotmart.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full sm:w-auto sm:inline-block text-center px-7 py-4 min-h-[56px] rounded-lg font-body type-button tracking-[0.06em] transition-all duration-300 active:scale-[0.98] hover:brightness-110"
                  style={{ background: ACCENT, color: '#ffffff' }}
                >
                  Acessar a Hotmart
                </a>
              </div>
            </div>
          </motion.div>

        </motion.div>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-8 text-center border-t mt-12"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="font-display italic text-[15px] mb-2" style={{ color: `${ACCENT_SOFT}99` }}>
          Com amor e presença, Mônica
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Estrelas de Luz
        </p>
      </footer>
    </div>
  )
}
